(ns app.front.view.bond.new
  (:require [app.domain.bond.model :as bond-model]
            [app.domain.business.model :as business-model]
            [app.domain.party.model :as party-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.radio :as ui-radio]
            [app.front.util :as futil]
            [app.front.validate :as validate]
            [app.front.view.attachment.list :as attachment-list]
            [app.front.view.bond.common :as bond-common]
            [app.front.view.bond.util :as bond-util]
            [app.front.view.business.search :as business-search]
            [app.front.view.workspace.util :as ws-util]
            [clojure.string :as str]
            [core.db.entity-api :as entity-api]
            [core.front.config :as front-config]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [re-frame.std-interceptors :as interceptors]
            [schema.core :as s]))

(def ^:private state-path (interceptors/path ::state))

(defn- apply-template-selection [state templates]
  (let [chosen-template (:selected-bond-template state)]
    (-> state
        (assoc :bond-templates templates)
        (cond-> chosen-template (assoc :selected-bond-template chosen-template))
        (cond-> (nil? chosen-template) (dissoc :selected-bond-template)))))

(defn- form-data [state selected-business]
  {:bond/owner        (cond
                        (:workspace-id state)
                        (:selected-bond-applicant state)

                        (= :bond.owner-type/business (:owner-type state))
                        selected-business

                        :else
                        (:new-bond-applicant-email state))
   :bond/owner-type   (:owner-type state)
   :bond/template     (:selected-bond-template state)
   :bond/external-id  (:new-bond-account-number state)
   :bond/amount       (:bond-value state)
   :bond/expires-on   (:bond-expires-on state)
   :bond/issuer-name  (:new-bond-company-name state)
   :bond/description  (:new-bond-description state)
   :bond/issuer-phone (:new-bond-company-phone state)
   :bond/issuer-email (:new-bond-company-email state)})

(defn- ws-entity->domain-group-id [workspace-entity ctx]
  (when (and workspace-entity ctx)
    (let [resolve-entity (fn [e]
                           (if (entities/entity-id? e)
                             (entities/find-entity ctx {:db/id e})
                             e))]
      (some-> workspace-entity
              :domain/authority-group
              resolve-entity
              :entity/parent
              resolve-entity
              :db/id))))

(rf/reg-sub ::state :-> ::state)

(rf/reg-sub ::selected-bond-template
  :<- [::state]
  :-> :selected-bond-template)

(rf/reg-sub ::bond-templates
  :<- [::state]
  :-> :bond-templates)

(rf/reg-sub ::bond-template-options
  :<- [::bond-templates]
  :<- [:language]
  (fn [[templates lang] _]
    (->> templates
         (filter du/is-active?)
         (map (fn [template]
                (let [raw-label (some-> (bond-model/template-name template lang) str/trim)
                      label     (or raw-label (str (:db/id template)))]
                  {:name label
                   :id   (:db/id template)})))
         (util/distinct-by :name)
         (sort-by (comp str/lower-case :name)))))

(rf/reg-sub ::bond-value
  :<- [::state]
  :-> :bond-value)

(rf/reg-sub ::selected-bond-expiration-date
  :<- [::state]
  :-> :bond-expires-on)

(rf/reg-sub ::new-bond-company-name
  :<- [::state]
  :-> :new-bond-company-name)

(rf/reg-sub ::new-bond-company-phone
  :<- [::state]
  :-> :new-bond-company-phone)

(rf/reg-sub ::new-bond-company-email
  :<- [::state]
  :-> :new-bond-company-email)

(rf/reg-sub ::selected-bond-applicant
  :<- [::state]
  :-> :selected-bond-applicant)

(rf/reg-sub ::new-bond-applicant-email
  :<- [::state]
  :-> :new-bond-applicant-email)

(rf/reg-sub ::new-bond-description
  :<- [::state]
  :-> :new-bond-description)

(rf/reg-sub ::new-bond-account-number
  :<- [::state]
  :-> :new-bond-account-number)

(rf/reg-sub ::submitting?
  :<- [::state]
  :-> :submitting?)

(rf/reg-sub ::show-errors?
  :<- [::state]
  :-> :show-errors?)

(rf/reg-sub ::bond-created?
  :<- [::state]
  :-> :bond-created?)

(rf/reg-sub ::created-bond-id
  :<- [::state]
  :-> :bond-db-id)

(rf/reg-sub ::bond-external-id-conflict?
  :<- [::state]
  :-> :bond-external-id-conflict?)

(rf/reg-sub ::bond-has-all-required-attachments?
  (fn [[_ bond-db-id]]
    (rf/subscribe [::bond-common/bond-attachment-requirements bond-db-id]))
  (fn [requirements _]
    (every? #(pos? (:requirement/attachment-count %)) requirements)))

(rf/reg-sub ::group-required-bond-fields
  :<- [::state]
  :-> :group/bond-required-fields)

(rf/reg-sub ::workspace-id
  :<- [::state]
  :-> :workspace-id)

;; Owner type selection for group-based bond creation (no workspace)
(def bond-owner-types
  "Owner types for bond creation without workspace"
  [:bond.owner-type/user
   :bond.owner-type/business])

(rf/reg-sub ::owner-type
  :<- [::state]
  :-> :owner-type)

(rf/reg-event-db ::set-owner-type state-path
  (fn [state [_ owner-type]]
    (assoc state :owner-type owner-type)))

(rf/reg-sub ::error-messages
  :<- [::group-required-bond-fields]
  :<- [::workspace-id]
  :<- [::owner-type]
  (fn [[required-fields workspace-id owner-type] _]
    (let [invite-flow? (nil? workspace-id)]
      (bond-util/effective-error-messages (util/mmap required-fields workspace-id owner-type invite-flow?)))))

(rf/reg-sub ::form-data
  (fn [db _]
    (let [selected-business (get-in db [::business-search/data ::business-search/selected-business])]
      (form-data (::state db) selected-business))))

(rf/reg-sub ::validator
  :<- [::form-data]
  :<- [::error-messages]
  (fn [[data error-messages] _]
    (validate/model-validator data error-messages)))

(rf/reg-sub ::form-valid?
  :<- [::form-data]
  :<- [::error-messages]
  (fn [[data error-messages] _]
    (empty? (validate/validate-form-strict data error-messages))))

(rf/reg-sub ::applicant-select-opts
  (fn [[_ workspace-id]]
    [(rf/subscribe [::entities/entity {:workspace/id workspace-id}])
     (rf/subscribe [:language])
     (rf/subscribe [:session/user])
     (rf/subscribe [:session/bond-admin?])])
  (fn [[workspace lang user is-bond-admin?] _]
    (when workspace
      (let [workspace-parties (party-model/workspace-parties workspace)
            party->option     (fn [party]
                                (when-let [applicant-id (or (get-in party [:party/user :db/id])
                                                            (get-in party [:party/business :db/id]))]
                                  {:name (party-model/full-name-with-roles lang party)
                                   :id   applicant-id}))]
        (if is-bond-admin?
          (->> workspace-parties
               (keep party->option)
               (util/distinct-by :id)
               (sort-by :name))
          (let [user-id   (:db/id user)
                my-party? (fn [party]
                            (or (= user-id (get-in party [:party/user :db/id]))
                                (->> (:party/business party)
                                     business-model/business-users
                                     (some #(= user-id (:db/id %))))))]
            (->> workspace-parties
                 (filter my-party?)
                 (keep party->option)
                 vec)))))))

(rf/reg-event-fx ::set-workspace-id state-path
  (fn [{state :db} [_ workspace-id]]
    {:db       (assoc state :workspace-id workspace-id)
     :dispatch [::find-or-fetch-workspace]}))

(defn find-workspace-entity
  [db workspace-id]
  (entities/find-entity (entities/db->context db)
                        {:workspace/id workspace-id}))

(defn workspace-domain-group-id
  [workspace-entity]
  (some-> workspace-entity :domain/authority-group :entity/parent :db/id))

(defn workspace-user-party
  [workspace user-id]
  (when workspace
    (p/find-first (party-model/workspace-parties workspace)
                  (comp (partial = user-id)
                        :db/id
                        :party/user))))

(defn party->selection
  [language party]
  (when party
    {:name (party-model/full-name-with-roles language party)
     :id   (-> party :party/user :db/id)}))

(rf/reg-event-fx ::load-bond-templates
  (fn [{:keys [db]} [_ domain-group-id]]
    (let [state    (or (::state db) {})
          loading? (:bond-templates-loading? state)]
      (if (or loading? (nil? domain-group-id))
        {}
        {:db           (assoc db ::state (assoc state :bond-templates-loading? true))
         :comm/command {:command    :bond-template/get-available-with-requirements
                        :method     :get
                        :data       {:group-id domain-group-id}
                        :on-success [::load-bond-templates.success]
                        :on-error   [::load-bond-templates.error]}}))))

(rf/reg-event-fx ::load-bond-templates.error
  (fn [_ _]
    {:fx [[:dispatch [:notifications/add :toast :error :error/error :error/bond-templates-not-loaded]]]}))

(rf/reg-event-fx ::load-bond-templates.success
  (fn [{:keys [db]} [_ status resp]]
    (let [templates            (:templates resp)
          attachment-types     (:attachment-types resp)
          all-entities         (concat templates attachment-types)
          all-entities-map     (du/entities->entity-db all-entities)
          state                (::state db)
          state-with-templates (-> state
                                   (assoc :bond-templates-loaded true)
                                   (apply-template-selection templates)
                                   (dissoc :bond-templates-loading?))]
      {:db (assoc db ::state state-with-templates)
       :fx [[:dispatch [::entities/merge-entity-db status all-entities-map]]]})))

(rf/reg-event-fx ::initialize-form
  (fn [{:keys [db]} [_ workspace-entity]]
    (let [party-user-ids             (if workspace-entity
                                       (->> (party-model/workspace-parties workspace-entity)
                                            (keep #(get-in % [:party/user :db/id]))
                                            set)
                                       #{})
          lang                       (:language db)
          session-user               (:session/user db)
          session-user-id            (:db/id session-user)
          session-user-party         (some #(when (= (get-in % [:party/user :db/id]) session-user-id) %)
                                           (party-model/workspace-parties workspace-entity))
          session-user-is-party      (and session-user-id
                                          (contains? party-user-ids session-user-id))
          domain-group               (some-> workspace-entity :domain/authority-group :entity/parent)
          group-required-bond-fields (:group/bond-required-fields domain-group)
          initial-state              (-> (::state db)
                                         (assoc :selected-bond-applicant
                                                (when session-user-is-party
                                                  {:name (party-model/full-name-with-roles lang session-user-party)
                                                   :id   session-user-id}))
                                         (assoc :group/bond-required-fields (or group-required-bond-fields #{})))]
      {:db (assoc db ::state initial-state)})))

(rf/reg-event-fx ::find-or-fetch-workspace
  (fn [{:keys [db]} _]
    (let [{:keys [workspace-id workspace-loading?]} (::state db)]
      (if workspace-loading?
        {}
        (if-let [workspace (find-workspace-entity db workspace-id)]
          {:dispatch-n [[::set-domain-group-id (workspace-domain-group-id workspace)]
                        [::select-bond-applicant (->> db :session/user :db/id
                                                      (workspace-user-party workspace)
                                                      (party->selection (:language db)))]]}
          {:db           (assoc-in db [::state :workspace-loading?] true)
           :comm/command {:command          :workspace/get
                          :data             {:workspace/id workspace-id
                                             :find-related [:entity.type/party
                                                            :entity.type/party-role]}
                          :on-success       [::find-or-fetch-workspace.success]
                          :finally-dispatch [::find-or-fetch-workspace.done]}})))))

(rf/reg-event-db ::find-or-fetch-workspace.done state-path
  (fn [state _]
    (assoc state :workspace-loading? false)))

(rf/reg-event-fx ::find-or-fetch-workspace.success
  (fn [{:keys [db]} [_ _ {entities :entities}]]
    (let [db           (entities/merge-entity-db db entities)
          workspace-id (-> db ::state :workspace-id)
          workspace    (find-workspace-entity db workspace-id)]
      {:dispatch-n [[::set-domain-group-id (workspace-domain-group-id workspace)]
                    [::select-bond-applicant (->> db :session/user :db/id
                                                  (workspace-user-party workspace)
                                                  (party->selection (:language db)))]]})))

(rf/reg-event-fx ::set-domain-group-id
  (fn [{:keys [db]} [_ domain-group-id]]
    (let [bond-config (select-keys (entity-api/entity (entities/db->context db) domain-group-id)
                                   bond-model/bond-group-config-keys)]
      {:db       (update db ::state merge (util/mmap domain-group-id) bond-config)
       :dispatch [(if (empty? bond-config) ::fetch-bond-config ::fetch-bond-templates)]})))

(rf/reg-event-fx ::fetch-bond-config
  (fn [{{::keys [state] :as db} :db} _]
    (if (:bond-config-loading? state)
      {}
      {:db           (assoc-in db [::state :bond-config-loading?] true)
       :comm/command {:command          :bond-config/get
                      :method           :get
                      :data             {:group-id (:domain-group-id state)}
                      :on-success       [::fetch-bond-config.success]
                      :finally-dispatch [::fetch-bond-config.done]}})))

(rf/reg-event-db ::fetch-bond-config.success state-path
  (fn [state [_ _ {:keys [config]}]]
    (merge state config)))

(rf/reg-event-fx ::fetch-bond-config.done
  (fn [{:keys [db]} _]
    {:db       (assoc-in db [::state :bond-config-loading?] false)
     :dispatch [::fetch-bond-templates]}))

(rf/reg-event-fx ::fetch-bond-templates state-path
  (fn [{{:keys [bond-templates-loading? domain-group-id] :as state} :db} _]
    (if (or bond-templates-loading? (nil? domain-group-id))
      {}
      {:db           (assoc state :bond-templates-loading? true)
       :comm/command {:command          :bond-template/get-all
                      :method           :get
                      :data             {:group-id domain-group-id}
                      :on-success       [::fetch-bond-templates.success]
                      :finally-dispatch [::fetch-bond-templates.done]}})))

(rf/reg-event-fx ::fetch-bond-templates.success
  (fn [{:keys [db]} [_ status {templates :entities}]]
    {:db       (assoc-in db [::state :bond-templates] templates)
     :dispatch [::entities/merge-entity-db status (du/entities->entity-db templates)]}))

(rf/reg-event-db ::fetch-bond-templates.done state-path
  (fn [state _]
    (assoc state :bond-templates-loading? false)))

(rf/reg-event-fx ::workspace-fetch.success
  (fn [{:keys [db]} [_ workspace-id _status data]]
    (let [workspace-entities (:entities data)
          db-with-entities   (entities/merge-entity-db db workspace-entities)
          ctx                (entities/db->context db-with-entities)
          workspace-entity   (entities/find-entity ctx {:workspace/id workspace-id})
          domain-group-id    (ws-entity->domain-group-id workspace-entity ctx)]
      {:db (assoc-in db-with-entities [::state :domain-group-id] domain-group-id)
       :fx [[:dispatch [::set-workspace-id workspace-id]]
            [:dispatch [::load-bond-templates domain-group-id]]
            [:dispatch [::initialize-form workspace-entity]]]})))

(rf/reg-event-fx ::bond-attachments-fetch.success
  (fn [_ [_ status resp]]
    {:fx [[:dispatch [::entities/merge-entity-db status resp]]]}))

(rf/reg-event-fx ::get-existing-bond-attachments
  (fn [{:keys [db]} [_ bond-db-id]]
    {:db           (update db ::state assoc
                           :bond-created?   true
                           :bond-db-id bond-db-id)
     :comm/command {:command    :bond/get-attachments
                    :method     :get
                    :data       {:bond-id bond-db-id}
                    :on-success [::bond-attachments-fetch.success]}}))

(rf/reg-event-fx ::bond-fetch.success
  (fn [{:keys [db]} [_ status resp]]
    (let [bond            (some->> resp vals (filter #(= (:entity/type %) :entity.type/bond)) first)
          bond-db-id      (:db/id bond)
          domain-group-id (:bond/group bond)]
      {:db (update db ::state assoc
                   :bond-created? true
                   :bond-db-id bond-db-id
                   :domain-group-id domain-group-id)
       :fx [[:dispatch [::entities/merge-entity-db status resp]]
            [:dispatch [::load-bond-templates domain-group-id]]
            [:dispatch [::get-existing-bond-attachments bond-db-id]]]})))

(rf/reg-event-fx ::initialize-attachments-section
  (fn [_ [_ bond-uuid]]
    {:comm/command {:command    :bond/get-bond
                    :method     :get
                    :data       {:bond-id bond-uuid}
                    :on-success [::bond-fetch.success]}}))

(rf/reg-event-fx ::pre-initialize-form
  (fn [{:keys [db]} [_ workspace-id bond-id]]
    (cond
      (and (nil? workspace-id) (nil? bond-id))
      {}

      bond-id
      {:fx [[:dispatch [::initialize-attachments-section bond-id]]]}

      :else
      (let [ctx              (entities/db->context db)
            workspace-entity (when (and workspace-id ctx)
                               (entities/find-entity ctx {:workspace/id workspace-id}))]
        (if workspace-entity
          (let [domain-group-id (ws-entity->domain-group-id workspace-entity ctx)]
            {:db (assoc-in db [::state :domain-group-id] domain-group-id)
             :fx [[:dispatch [::set-workspace-id workspace-id]]
                  [:dispatch [::load-bond-templates domain-group-id]]
                  [:dispatch [::initialize-form workspace-entity]]]})
          {:comm/command {:command    :workspace/get
                          :data       {:workspace/id workspace-id
                                       :find-related [:entity.type/party
                                                      :entity.type/party-role]}
                          :on-success [::workspace-fetch.success workspace-id]}})))))

(rf/reg-event-db ::select-bond-template state-path
  (fn [state [_ template]]
    (assoc state :selected-bond-template template)))

(rf/reg-event-db ::set-bond-value state-path
  (fn [state [_ value]]
    (assoc state :bond-value value)))

(rf/reg-event-db ::set-bond-expiry state-path
  (fn [state [_ date]]
    (assoc state :bond-expires-on date)))

(rf/reg-event-db ::set-bond-company-name state-path
  (fn [state [_ name]]
    (assoc state :new-bond-company-name name)))

(rf/reg-event-db ::set-bond-company-email state-path
  (fn [state [_ email]]
    (assoc state :new-bond-company-email email)))

(rf/reg-event-db ::set-bond-company-phone state-path
  (fn [state [_ phone]]
    (assoc state :new-bond-company-phone phone)))

(rf/reg-event-db ::select-bond-applicant state-path
  (fn [state [_ applicant]]
    (assoc state :selected-bond-applicant applicant)))

(rf/reg-event-db ::set-bond-applicant-email state-path
  (fn [state [_ applicant]]
    (assoc state :new-bond-applicant-email applicant)))

(rf/reg-event-db ::set-bond-description state-path
  (fn [state [_ description]]
    (assoc state :new-bond-description description)))

(rf/reg-event-db ::set-bond-account-number state-path
  (fn [state [_ bond-number]]
    (-> state
        (assoc :new-bond-account-number bond-number)
        (dissoc :bond-external-id-conflict?))))

(defn- from-wizard? [db]
  (-> db :route/match :parameters :query :wizard))

(defn- workspace-return-route [db workspace-id]
  (if (from-wizard? db)
    {:route  :view/application-wizard-workspace
     :params {:workspace-id workspace-id
              :step         "summary"}}
    {:route  :view/workspace
     :params {:workspace-id workspace-id}}))

(rf/reg-event-fx ::create-bond.success
  (fn [{:keys [db]} [_ {:keys [workspace-id group-id]} status resp]]
    (when (= status 200)
      (let [bond-db-id       (entities/id-of-some-in-apply-changes :entity.type/bond resp)
            bond-entity      (get resp bond-db-id)
            bond-external-id (second (:bond/id bond-entity))]
        (if workspace-id
          ;; Workspace flow: redirect to attachment upload page
          {:fx         [[:dispatch [::entities/apply-changes status resp]]]
           :db         (update db ::state assoc
                               :bond-created? true
                               :bond-db-id bond-db-id
                               :bond-external-id bond-external-id
                               :submitting? false)
           :route/push {:route :view/bond.new
                        :query (cond-> {:workspace workspace-id
                                        :bond      bond-external-id}
                                 (from-wizard? db)
                                 (assoc :wizard true))}}
          ;; Domain admin flow: invitation sent, redirect back to bond management
          {:db         (dissoc db ::state)
           :route/push {:route :view/group.admin.bonds :params {:group-id group-id}}
           :fx         [[:dispatch [::entities/apply-changes status resp]]
                        [:dispatch [:notifications/add :toast :success :success :bond.invite/success]]]})))))



(rf/reg-event-fx ::create-bond.error
  (fn [{:keys [db]} [_ _ body]]
    (let [error               (:error body)
          bond-number-exists? (= error :db.error/unique-conflict)]
      (cond-> {:db (update db ::state (fn [state]
                                        (cond-> (dissoc state :submitting?)
                                          bond-number-exists? (assoc :bond-external-id-conflict? true))))}
        (not bond-number-exists?)
        (assoc :fx [[:dispatch [:notifications/add :toast :error :error/error (or error :error/bond-application-create-failed)]]])))))

(rf/reg-event-fx ::cancel-bond-attachments
  (fn [{:keys [db]} [_ workspace-id]]
    {:db         (dissoc db ::state)
     :route/push (workspace-return-route db workspace-id)
     :fx         [[:dispatch [:notifications/add :toast :success :success :bond.new/save-success-missing-attachments]]]}))

(rf/reg-event-fx ::cancel-bond-application
  (fn [{:keys [db]} [_ {:keys [workspace group]}]]
    {:db         (dissoc db ::state)
     :route/push (if (nil? workspace)
                   {:route :view/group.admin.bonds :params {:group-id group}}
                   (workspace-return-route db workspace))}))

(rf/reg-event-fx ::complete-bond-setup
  (fn [{:keys [db]} [_ workspace-id]]
    {:db         (dissoc db ::state)
     :route/push (workspace-return-route db workspace-id)
     :fx         [[:dispatch [:notifications/add :toast :success :success :bond.new/save-success]]]}))

(rf/reg-event-fx ::validate-and-send
  (fn [{:keys [db]} [_ workspace-id]]
    (let [state             (get db ::state {})
          owner-type        (:owner-type state)
          selected-business (get-in db [::business-search/data ::business-search/selected-business])
          data              (form-data state selected-business)
          required-fields   (:group/bond-required-fields state)
          invite-flow?      (nil? workspace-id)
          error-messages    (bond-util/effective-error-messages
                              (util/mmap required-fields workspace-id owner-type invite-flow?))
          validation-errors (validate/validate-form-strict data error-messages)
          valid?            (empty? validation-errors)]
      (if-not valid?
        {:db (assoc db ::state (assoc state :show-errors? true))}
        (let [domain-group-id  (or (:domain-group-id state)
                                   (let [ctx       (entities/db->context db)
                                         workspace (entities/find-entity ctx {:workspace/id workspace-id})]
                                     (-> workspace :domain/authority-group :entity/parent :db/id)))
              form-data-params (bond-util/form-data->api-params data)
              owner            (if workspace-id
                                 (-> state :selected-bond-applicant :id)
                                 (when (= owner-type :bond.owner-type/user)
                                   (-> state :new-bond-applicant-email)))
              request-data     (assoc form-data-params :bond/owner owner)
              business-id      (when (and (not workspace-id)
                                          (= owner-type :bond.owner-type/business))
                                 (:business/unique-id selected-business))
              request          (p/assoc-if
                                 {:group-id domain-group-id
                                  :data     request-data}
                                 :workspace-id workspace-id
                                 :business/unique-id business-id)
              command          {:command         (if workspace-id
                                                   :bond/create-bond-application
                                                   :bond/invite-owner)
                                :method          :post
                                :data            request
                                :on-success      [::create-bond.success (util/mmap {:group-id domain-group-id} workspace-id)]
                                :on-bad-request  [::create-bond.error]
                                :on-server-error [::create-bond.error]}]
          {:db       (assoc db ::state (assoc state :submitting? true :show-errors? true))
           :dispatch [:comm/command-monitored command]})))))


(defn bond-applicant-email-field [tr]
  (let [show-errors? (<sub [::show-errors?])
        validator    (<sub [::validator])]
    [:div.flex-1.min-w-0
     [ui/text-input
      {:id                 "add-bond-applicant-email"
       :wrapper-class-name "w-full"
       :hide-unit?         true
       :value              (<sub [::new-bond-applicant-email])
       :required           true
       :invalid            (validate/field-invalid? show-errors? validator :bond/owner)
       :invalid-text       (validate/field-invalid-text show-errors? validator :bond/owner tr)
       :on-change          #(>evt [::set-bond-applicant-email (futil/tval %)])
       :label-text         (tr [:bond/applicant-email])}]]))

(defn bond-owner-selector
  "Component for selecting a bond owner type (individual or business) when creating
   a bond without a workspace. Shows radio buttons and conditionally displays
   either an email input or business search."
  [tr]
  (let [owner-type        (<sub [::owner-type])
        lang              (<sub [:language])
        selected-business (<sub [::business-search/selected-business])
        show-errors?      (<sub [::show-errors?])
        validator         (<sub [::validator])
        owner-invalid?    (validate/field-invalid? show-errors? validator :bond/owner)]
    [:div.flex.flex-col.gap-4.mb-4
     [ui-radio/radio {:name      "bond-owner-type"
                      :values    bond-owner-types
                      :value     owner-type
                      :val->str  (fn [v] (tr [v]))
                      :on-change (fn [v] (>evt [::set-owner-type v]))}]
     (case owner-type
       :bond.owner-type/business
       [:div.mt-2.flex-1.min-w-0
        (if selected-business
          [business-search/selected-business-display (util/mmap tr lang)]
          [:<>
           [business-search/authority-existing-business-search
            (util/mmap tr lang)]
           (when owner-invalid?
             [:div.mt-2.text-danger.text-sm
              (tr [:bond/validation-business-required])])])]

       :bond.owner-type/user
       [bond-applicant-email-field tr]

       ;; default - show email field if owner-type not yet set
       [bond-applicant-email-field tr])]))

(rf/reg-sub ::back-href
  :<- [:route/match]
  (fn [match _]
    (let [{:keys [workspace group wizard]} (-> match :parameters :query)]
      (cond
        group
        (href/href :view/group.admin.bonds {:group-id group})

        wizard
        (href/href :view/application-wizard-workspace
                   {:workspace-id workspace
                    :step         "summary"})

        :else
        (ws-util/workspace-href {:workspace/id workspace})))))

(rf/reg-sub ::view-data
  :<- [::back-href]
  (fn [back-href]
    {:back-href back-href}))

(defn attachments-section [tr workspace-id]
  (when-let [bond-db-id (<sub [::created-bond-id])]
    (let [ctx                           (<sub [::entities/context])
          bond                          (entities/find-entity ctx {:db/id bond-db-id})
          has-all-required-attachments? (<sub [::bond-has-all-required-attachments? bond-db-id])]
      [:div.mt-8
       [:h3.mb-4 (tr [:bond/required-attachments])]
       [bond-common/bond-attachment-requirements-list tr bond-db-id has-all-required-attachments?]
       [:div.mb-6
        [attachment-list/list-view {:target         bond
                                    :can-edit-bond? true}]]
       [:div.flex.flex-row.gap-4.mt-8
        [ui/button
         {:id       "cancel-bond-creation"
          :kind     :secondary
          :on-click #(>evt [::cancel-bond-attachments workspace-id])}
         (tr [:bond.new/modal-cancel-attachments])]
        [ui/button
         {:id       "complete-bond-setup"
          :kind     :primary
          :disabled (not has-all-required-attachments?)
          :on-click #(>evt [::complete-bond-setup workspace-id])}
         (tr [:bond.new/complete-setup])]]])))

(defn new-bond [{{{:keys [workspace group]} :query} :parameters
                 :as                                match}]
  (let [tr              (<sub [:tr])
        submitting?     (or (<sub [::submitting?]) false)
        show-errors?    (<sub [::show-errors?])
        validator       (<sub [::validator])
        form-valid?     (<sub [::form-valid?])
        bond-created?   (<sub [::bond-created?])
        required-fields (<sub [::group-required-bond-fields])
        conflict?       (<sub [::bond-external-id-conflict?])
        view-state      (<sub [::state])
        invite-flow?    (nil? (:workspace-id view-state))]
    [:div.cloudpermit-page
     [ui/page-header (util/mmap match)
      [:div.px-8.flex.justify-between
       [:h2 (tr [:bond.new/account-setup])]]]
     [:section.flex.flex-col.w-full
      (if bond-created?
        [attachments-section tr workspace]
        [:<>
         [bond-common/bond-form-row
          (if (:workspace-id view-state)
            [bond-common/applicant-selector
             {:tr           tr
              :value        (<sub [::selected-bond-applicant])
              :options      (<sub [::applicant-select-opts workspace])
              :show-errors? show-errors?
              :validator    validator
              :on-change    #(>evt [::select-bond-applicant %])}]
            [bond-owner-selector tr])]
         [bond-common/bond-form-row
          [bond-common/template-selector
           {:tr           tr
            :value        (<sub [::selected-bond-template])
            :options      (<sub [::bond-template-options])
            :show-errors? show-errors?
            :validator    validator
            :on-change    #(>evt [::select-bond-template %])}]
          [bond-common/external-id-input
           {:tr           tr
            :value        (<sub [::new-bond-account-number])
            :show-errors? show-errors?
            :validator    validator
            :on-change    #(>evt [::set-bond-account-number (futil/tval %)])
            :conflict?    conflict?
            :required     (some? (:workspace-id view-state))}]
          [bond-common/amount-input
           {:tr           tr
            :value        (<sub [::bond-value])
            :show-errors? show-errors?
            :validator    validator
            :required?    (not invite-flow?)
            :on-change    #(>evt [::set-bond-value %])}]]
         [bond-common/bond-form-row
          [bond-common/expiration-date-picker
           {:tr           tr
            :value        (<sub [::selected-bond-expiration-date])
            :show-errors? show-errors?
            :validator    validator
            :required?    (not invite-flow?)
            :on-change    #(>evt [::set-bond-expiry %])}]
          [bond-common/company-name-input
           {:tr           tr
            :value        (<sub [::new-bond-company-name])
            :show-errors? show-errors?
            :validator    validator
            :required?    (not invite-flow?)
            :on-change    #(>evt [::set-bond-company-name (futil/tval %)])}]
          [bond-common/company-phone-input
           {:tr           tr
            :value        (<sub [::new-bond-company-phone])
            :show-errors? show-errors?
            :validator    validator
            :required?    (and (not invite-flow?) (contains? required-fields :bond/issuer-phone))
            :country      (<sub [::front-config/country])
            :on-change    #(>evt [::set-bond-company-phone %])}]
          [bond-common/company-email-input
           {:tr           tr
            :value        (<sub [::new-bond-company-email])
            :show-errors? show-errors?
            :validator    validator
            :required?    (and (not invite-flow?) (contains? required-fields :bond/issuer-email))
            :on-change    #(>evt [::set-bond-company-email (futil/tval %)])}]]
         [bond-common/description-input
          {:tr           tr
           :value        (<sub [::new-bond-description])
           :show-errors? show-errors?
           :validator    validator
           :required?    (not invite-flow?)
           :on-change    #(>evt [::set-bond-description (futil/tval %)])}]
         [:div.flex.flex-row.gap-4.mt-8
          [ui/button
           {:id       "cancel-bond-creation"
            :kind     :secondary
            :on-click #(>evt [::cancel-bond-application {:workspace workspace :group group}])}
           (tr [:bond/modal-cancel])]
          [ui/button
           {:id       (if workspace "add-bond-attachments" "send-bond-invitation")
            :kind     :primary
            :disabled (or submitting? (not form-valid?))
            :on-click #(>evt [::validate-and-send workspace])}
           (tr (if workspace
                 [:bond.new/continue-to-attachments]
                 [:bond.invite/send]))]]])]]))

;; The navigation from the bond admin view will provide the group parameter
;; and navigation from a workspace view will provide the workspace parameter.
(def route
  {:name        :view/bond.new
   :view        new-bond
   :view-sub    [::view-data]
   :parameters  {:query {(s/optional-key :workspace) s/Str
                         (s/optional-key :bond)      s/Str
                         (s/optional-key :group)     s/Str
                         (s/optional-key :wizard)    s/Bool}}
   :controllers [{:parameters {:query [:workspace :group :bond]}
                  :start      (fn [{{:keys [workspace group bond]} :query}]
                                (if workspace
                                  (>evt [::pre-initialize-form workspace bond])
                                  (do (>evt [::set-domain-group-id (parse-long group)])
                                      (>evt [::set-owner-type :bond.owner-type/user])
                                      (>evt [::business-search/init]))))}]})
